import { createContext, useCallback, useContext, useState } from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import { useLogin, useRefreshToken } from '@/services/auth';
import { endPreview, getPreviewSession } from '@/lib/preview';

export const AuthContext = createContext<{
  isAuthenticated: boolean;
  loginMutation: UseMutationResult<
    { access: string; refresh: string },
    Error,
    { email: string; password: string }
  >;
  logout: () => void;
  getAccessToken: () => Promise<string>;
  refreshTokenIfNeeded: () => Promise<boolean>;
}>({
  isAuthenticated: false,
  loginMutation: {} as UseMutationResult<
    { access: string; refresh: string },
    Error,
    { email: string; password: string }
  >,
  logout: () => {},
  getAccessToken: () => Promise.resolve(''),
  refreshTokenIfNeeded: () => Promise.resolve(false),
});

const parseJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

const isTokenExpired = (token: string) => {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  // exp is in seconds
  return Date.now() >= payload.exp * 1000;
};

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export const AuthProvider = (props: { children?: React.ReactNode }) => {
  const { children } = props;
  // Portal preview sessions bypass normal auth entirely (per-tab, no refresh;
  // the preview simply ends when the admin's access token expires).
  const preview = getPreviewSession();
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    localStorage.getItem(ACCESS_TOKEN_KEY)
  );
  const [refreshToken, setRefreshToken] = useState<string | null>(() =>
    localStorage.getItem(REFRESH_TOKEN_KEY)
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  const clearTokens = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }, []);

  const saveTokens = useCallback((access: string, refresh: string) => {
    setAccessToken(access);
    setRefreshToken(refresh);
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  }, []);

  const refreshAccessTokenMutation = useRefreshToken(
    ({ access }) => {
      saveTokens(access, refreshToken!);
    },
    (error) => {
      console.error(error);
      clearTokens();
    }
  );

  const refreshTokenIfNeeded = useCallback(async () => {
    if (!accessToken || !refreshToken) return false;
    if (!isTokenExpired(accessToken)) return true;
    if (isRefreshing) return false;

    try {
      setIsRefreshing(true);
      const { access } =
        await refreshAccessTokenMutation.mutateAsync(refreshToken);
      return !!access;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      clearTokens();
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [
    accessToken,
    refreshToken,
    isRefreshing,
    refreshAccessTokenMutation,
    clearTokens,
  ]);

  const isAuthenticated = preview
    ? !isTokenExpired(preview.access)
    : !!accessToken &&
      (!isTokenExpired(accessToken) ||
        (!!refreshToken && !isTokenExpired(refreshToken)));

  const loginMutation = useLogin(
    ({ access, refresh }) => {
      saveTokens(access, refresh);
    },
    (error) => {
      console.error(error);
      clearTokens();
    }
  );

  const logout = useCallback(() => {
    if (preview) {
      endPreview();
      window.close();
      // Fallback when the browser refuses to close the tab
      window.location.reload();
      return;
    }
    clearTokens();
  }, [preview, clearTokens]);

  const getAccessToken = useCallback(async () => {
    if (preview) return preview.access;
    if (!accessToken) throw new Error('No access token');
    if (!isTokenExpired(accessToken)) return accessToken;
    const { access } = await refreshAccessTokenMutation.mutateAsync(
      refreshToken!
    );
    return access;
  }, [preview, accessToken, refreshToken, refreshAccessTokenMutation]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        loginMutation,
        logout,
        getAccessToken,
        refreshTokenIfNeeded,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('Auth context is not provided');
  }
  return context;
};
